import { z } from "zod";

// TES-296 (spec §16) — structured audit for sensitive CHANGES. The forensic
// AuditLog stays append-only + flat for the common case; these typed fields
// promote the spec's change-history dimensions off ad-hoc metadata JSON so
// medical decisions, feedback, program changes and flow starts carry a full,
// queryable "who did what, from what, why, was it patient-visible" record.

// Actor-role attribution: a single change can involve several roles (AI
// generated → coach edited → doctor approved → doctor published). All values
// are actor ids (employee id / "system"); NOT PII, so they live in a column
// outside the metadata PII scrub.
export const auditActorAttributionSchema = z.object({
  createdBy: z.string().optional(),
  generatedBy: z.string().optional(),
  editedBy: z.string().optional(),
  approvedBy: z.string().optional(),
  publishedBy: z.string().optional(),
  rejectedBy: z.string().optional(),
});
export type AuditActorAttribution = z.infer<typeof auditActorAttributionSchema>;

// Was the changed content visible to the patient, or internal (clinician-only
// / draft / rejected)? Drives the PDL "what did the patient actually see".
export const PATIENT_VISIBILITY = ["visible", "internal"] as const;
export const patientVisibilitySchema = z.enum(PATIENT_VISIBILITY);
export type PatientVisibility = z.infer<typeof patientVisibilitySchema>;

// The full structured-change contract, layered on top of the base audit
// input. Every field optional — a plain audit row sets none of them.
export const structuredAuditFieldsSchema = z.object({
  // Justification for the change (a clinician's stated reason). A first-class
  // audit field — retained verbatim, NOT run through the metadata PII scrub,
  // because the medical-decision audit trail is exactly what it exists for.
  reason: z.string().optional(),
  // Links this audit row to the TES-280 DomainEvent that triggered the change
  // (soft reference — no FK: the audit log outlives events, and `technical`
  // events are pruned by retention).
  sourceEventId: z.string().optional(),
  patientVisibility: patientVisibilitySchema.optional(),
  // Before/after version handles for versioned entities (flows, templates,
  // review items) — free strings so any versioning scheme fits.
  previousVersion: z.string().optional(),
  newVersion: z.string().optional(),
  attribution: auditActorAttributionSchema.optional(),
});
export type StructuredAuditFields = z.infer<typeof structuredAuditFieldsSchema>;
